/**
 * F10 — typed wrappers for the admin venue endpoints.
 *
 * Every backend endpoint wraps its payload in the shared `{ data, … }` admin
 * envelope — the wrappers below unwrap it so callers get the bare shape.
 *
 * Endpoints covered:
 *   POST   /admin/venues                          → create
 *   PATCH  /admin/venues/:id/slug                 → update slug ({ newSlug })
 *   PATCH  /admin/venues/:id/status               → update status
 *   PATCH  /admin/venues/:id/tier                 → update tier
 *   PATCH  /admin/venues/:id/claim-status         → update claim status
 *   POST   /admin/venues/:id/photos/upload-url    → presigned upload URL
 *   POST   /admin/venues/:id/photos               → attach photo after upload
 *   GET    /admin/venues/submissions               → list submissions
 *   GET    /admin/venues/submissions/:id           → submission detail
 *   POST   /admin/venues/submissions/:id/approve   → approve submission
 *   POST   /admin/venues/submissions/:id/reject    → reject submission
 *   POST   /admin/venues/submissions/:id/merge     → merge into existing venue
 *   GET    /admin/venues/edits                     → list edits
 *   GET    /admin/venues/edits/:id                 → edit detail
 *   POST   /admin/venues/edits/:id/apply           → apply edit
 *   POST   /admin/venues/edits/:id/reject          → reject edit
 *
 * 🔴 V-14 cleanup (2026-06): the dead F10 methods superseded by the F42 ops
 * surface were REMOVED rather than repaired — `listVenues`/`getVenue`/
 * `updateVenue` (superseded by `adminVenuesApi.list/getDetail/editFields`),
 * `mergeVenue` (superseded by the F42 merge wizard → `adminVenuesMergeApi`),
 * and the photo-management trio `updatePhoto`/`reorderPhotos`/`deletePhoto`/
 * `setCoverPhoto` + `triggerRepublish`, all of which had ZERO callers AND
 * method/path/body/envelope mismatches against the live backend. The backend
 * routes remain; resurfacing admin photo management is a deliberate follow-up
 * (the F42 detail page shows photos read-only today). The kept lifecycle
 * methods (slug/status/tier/claim-status) were repaired to the real contract
 * and are consumed by the V-07 "Status & tier" section on the detail page.
 */
import type {
  AdminPageMeta,
  Venue,
  VenueStatus,
  VenueTier,
  VenueClaimStatus,
  VenuePhoto,
  VenueSubmission,
  VenueSubmissionStatus,
  VenueEdit,
  VenueEditStatus,
  CreateVenueInput,
  RejectVenueSubmissionInput,
  MergeVenueSubmissionInput,
  RejectVenueEditInput,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient.js';

// ─── Shared response envelopes ─────────────────────────────────────────────

/** Presigned PUT issued by POST /admin/venues/:id/photos/upload-url. */
export interface VenuePhotoUploadUrlResponse {
  readonly url: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly s3Key: string;
  readonly expiresAt: string;
}

export interface VenueSubmissionListResponse {
  readonly items: readonly VenueSubmissionForAdmin[];
  readonly nextCursor: string | null;
  readonly totalCount?: number;
  /** Present when `page` is supplied (page-numbered pagination). */
  readonly pagination?: AdminPageMeta;
}

export interface VenueSubmissionForAdmin extends VenueSubmission {
  readonly submitterDisplayName?: string;
  readonly potentialDuplicateVenue?: Pick<Venue, 'id' | 'slug' | 'name'> | null;
}

/**
 * Fields an admin may override when approving a submission. Matches the API's
 * `POST /admin/venues/submissions/:id/approve` body (`overrides`): the row is
 * merged into the submission payload before the venue is created. Location
 * (lat/lng) + the core text fields are editable here; sports / region / hours /
 * links are refined on the venue page after approval.
 */
export interface ApproveVenueOverrides {
  readonly name?: string;
  readonly phone?: string;
  readonly whatsappPhone?: string | null;
  readonly streetAddress?: string | null;
  readonly description?: string | null;
  readonly lat?: number;
  readonly lng?: number;
  readonly sports?: readonly string[];
  readonly primarySport?: string;
}

export interface VenueEditListResponse {
  readonly items: readonly VenueEditListItem[];
  readonly nextCursor: string | null;
  readonly totalCount?: number;
  /** Present when `page` is supplied (page-numbered pagination). */
  readonly pagination?: AdminPageMeta;
}

/**
 * A row in the edits queue. The `GET /admin/venues/edits` endpoint returns a
 * flat, venue-enriched shape (`venueName`/`venueSlug`/`fieldCount`) — NOT the
 * nested `venue` object of the detail endpoint — so the queue table can render
 * a venue name + change count without a per-row fetch.
 */
export interface VenueEditListItem {
  readonly id: string;
  readonly venueId: string;
  readonly venueName: string | null;
  readonly venueSlug: string | null;
  readonly status: VenueEditStatus;
  readonly fieldCount: number;
  readonly submittedByUserId: string | null;
  readonly submitterEmail: string | null;
  readonly countryCode: string;
  readonly createdAt: string;
}

/** Resolved identity of whoever suggested the edit (admin "Submitted by" panel). */
export interface VenueEditSubmitter {
  readonly userId: string | null;
  readonly email: string | null;
  readonly displayName: string | null;
  readonly isRegistered: boolean;
}

/** The detail-endpoint shape, flattened by `getEdit` from `{ edit, currentVenue, submitter }`. */
export interface VenueEditForAdmin extends VenueEdit {
  readonly venue: Pick<Venue, 'id' | 'slug' | 'name'>;
  readonly fieldCount: number;
  readonly submitter?: VenueEditSubmitter;
}

// ─── List / filter option types ────────────────────────────────────────────

export interface VenueSubmissionListOpts {
  readonly status?: VenueSubmissionStatus;
  readonly countryCode?: string;
  readonly potentialDuplicate?: boolean;
  readonly cursor?: string;
  readonly limit?: number;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
}

export interface VenueEditListOpts {
  readonly status?: VenueEditStatus;
  readonly venueId?: string;
  readonly cursor?: string;
  readonly limit?: number;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
}

// ─── Venue CRUD ────────────────────────────────────────────────────────────

export const adminVenueApi = {
  // ── Create ─────────────────────────────────────────────────────────────
  // Backend returns 201 `{ data: venue }` (admin-venues.ts:244) — unwrap so the
  // create wizard can navigate with `result.id`. (V-02 fix: the old client
  // returned the raw envelope, so every successful create surfaced as an error
  // banner and risked duplicate resubmits.)
  createVenue: async (input: CreateVenueInput): Promise<Venue> => {
    const res = await adminFetch<{ readonly data: Venue }>('/admin/venues', {
      method: 'POST',
      body: input as Record<string, unknown>,
    });
    return res.data;
  },

  // ── Slug ───────────────────────────────────────────────────────────────
  // Backend: PATCH + strict `{ newSlug }` (admin-venues.ts:64-72/293).
  updateSlug: async (id: string, newSlug: string): Promise<void> => {
    await adminFetch(`/admin/venues/${id}/slug`, {
      method: 'PATCH',
      body: { newSlug },
    });
  },

  // ── Status ─────────────────────────────────────────────────────────────
  // Backend: strict `{ status, mergedIntoVenueId? }` (admin-venues.ts:74-79).
  updateStatus: async (
    id: string,
    status: VenueStatus,
    opts?: { mergedIntoVenueId?: string },
  ): Promise<void> => {
    await adminFetch(`/admin/venues/${id}/status`, {
      method: 'PATCH',
      body: {
        status,
        ...(opts?.mergedIntoVenueId !== undefined
          ? { mergedIntoVenueId: opts.mergedIntoVenueId }
          : {}),
      },
    });
  },

  // ── Tier ───────────────────────────────────────────────────────────────
  updateTier: async (id: string, tier: VenueTier): Promise<void> => {
    await adminFetch(`/admin/venues/${id}/tier`, {
      method: 'PATCH',
      body: { tier },
    });
  },

  // ── Claim status ───────────────────────────────────────────────────────
  updateClaimStatus: async (
    id: string,
    claimStatus: VenueClaimStatus,
    ownerB2bAccountId?: string | null,
  ): Promise<void> => {
    await adminFetch(`/admin/venues/${id}/claim-status`, {
      method: 'PATCH',
      body: {
        claimStatus,
        ...(ownerB2bAccountId !== undefined ? { ownerB2bAccountId } : {}),
      },
    });
  },

  // ── Photos ─────────────────────────────────────────────────────────────
  // Backend requires `originalFilename` (strict schema, admin-venues.ts:101-107)
  // and returns `{ data: { url, headers, s3Key, expiresAt } }`.
  getPhotoUploadUrl: async (
    venueId: string,
    body: { mime: string; sizeBytes: number; originalFilename: string },
  ): Promise<VenuePhotoUploadUrlResponse> => {
    const res = await adminFetch<{ readonly data: VenuePhotoUploadUrlResponse }>(
      `/admin/venues/${venueId}/photos/upload-url`,
      { method: 'POST', body },
    );
    return res.data;
  },

  // 🔴 The route's strict AttachPhotoBodySchema accepts ONLY
  // `{ s3Key, mime, sizeBytes, altText? }` (admin-venues.ts:109-116) — the
  // cdnUrl is computed server-side, so do NOT send `CreateVenuePhotoInput`.
  attachPhoto: async (
    venueId: string,
    body: { s3Key: string; mime: string; sizeBytes: number; altText?: string },
  ): Promise<VenuePhoto> => {
    const res = await adminFetch<{ readonly data: VenuePhoto }>(`/admin/venues/${venueId}/photos`, {
      method: 'POST',
      body,
    });
    return res.data;
  },

  // ─── Submissions ────────────────────────────────────────────────────────
  // Backend routes live at `/admin/venues/submissions*` (server.ts registers
  // admin-venue-submissions under the `/admin` prefix), and every endpoint
  // wraps its payload in the shared `{ data, … }` admin envelope. The old
  // client hit a non-existent `/admin/venues/submissions` path (→ 404 on the
  // sidebar badge poll AND the queue/detail pages) and mis-typed the body as
  // `{ items }`. Fix both: correct path + unwrap `data` (→ `items` for lists),
  // with `?? []` defaults so a missing body degrades gracefully.

  listSubmissions: async (opts?: VenueSubmissionListOpts): Promise<VenueSubmissionListResponse> => {
    const res = await adminFetch<{
      readonly data: readonly VenueSubmissionForAdmin[];
      readonly nextCursor: string | null;
      readonly pagination?: AdminPageMeta;
    }>(
      `/admin/venues/submissions${buildQuery({
        status: opts?.status,
        countryCode: opts?.countryCode,
        potentialDuplicate: opts?.potentialDuplicate,
        cursor: opts?.cursor,
        limit: opts?.limit,
        page: opts?.page,
        perPage: opts?.perPage,
      })}`,
    );
    return {
      items: res.data ?? [],
      nextCursor: res.nextCursor ?? null,
      ...(res.pagination !== undefined ? { pagination: res.pagination } : {}),
    };
  },

  getSubmission: async (id: string): Promise<VenueSubmissionForAdmin> => {
    // The detail endpoint returns a RICHER shape than the list: the row is
    // nested under `submission`, alongside `photos` + an optional duplicate
    // `candidateVenue` (`{ data: { submission, photos, candidateVenue } }`).
    // Flatten it to the page's flat `VenueSubmissionForAdmin` and map
    // `candidateVenue` → `potentialDuplicateVenue` so the review page reads
    // `submission.payload` / `.potentialDuplicateVenue` directly. (The old code
    // returned the wrapper as-is, so the page crashed on `submission.payload`.)
    const res = await adminFetch<{
      readonly data: {
        readonly submission: VenueSubmission;
        readonly photos?: readonly unknown[];
        readonly candidateVenue?: Pick<Venue, 'id' | 'slug' | 'name'> | null;
      };
    }>(`/admin/venues/submissions/${id}`);
    const { submission, candidateVenue } = res.data;
    return {
      ...submission,
      ...(candidateVenue ? { potentialDuplicateVenue: candidateVenue } : {}),
    };
  },

  approveSubmission: async (
    id: string,
    overrides?: ApproveVenueOverrides,
  ): Promise<{ venueId: string }> => {
    const res = await adminFetch<{ readonly data: { venueId: string } }>(
      `/admin/venues/submissions/${id}/approve`,
      { method: 'POST', body: { overrides } },
    );
    return res.data;
  },

  rejectSubmission: (id: string, reason: string): Promise<unknown> =>
    adminFetch(`/admin/venues/submissions/${id}/reject`, {
      method: 'POST',
      body: { reason } satisfies RejectVenueSubmissionInput,
    }),

  mergeSubmission: (id: string, mergeIntoVenueId: string, reason: string): Promise<unknown> =>
    adminFetch(`/admin/venues/submissions/${id}/merge`, {
      method: 'POST',
      body: { mergeIntoVenueId, reason } satisfies MergeVenueSubmissionInput,
    }),

  // ─── Edits ──────────────────────────────────────────────────────────────
  // Backend routes live at `/admin/venues/edits*` with the same `{ data, … }`
  // envelope (see Submissions note above).

  listEdits: async (opts?: VenueEditListOpts): Promise<VenueEditListResponse> => {
    const res = await adminFetch<{
      readonly data: readonly VenueEditListItem[];
      readonly nextCursor: string | null;
      readonly pagination?: AdminPageMeta;
    }>(
      `/admin/venues/edits${buildQuery({
        status: opts?.status,
        venueId: opts?.venueId,
        cursor: opts?.cursor,
        limit: opts?.limit,
        page: opts?.page,
        perPage: opts?.perPage,
      })}`,
    );
    return {
      items: res.data ?? [],
      nextCursor: res.nextCursor ?? null,
      ...(res.pagination !== undefined ? { pagination: res.pagination } : {}),
    };
  },

  getEdit: async (id: string): Promise<VenueEditForAdmin> => {
    // The detail endpoint returns `{ data: { edit, currentVenue, submitter } }` —
    // NOT a flat edit. Flatten it to the page's `VenueEditForAdmin` (nested
    // `venue` + derived `fieldCount` + resolved `submitter`), mirroring
    // `getSubmission`. (The old code returned the wrapper as-is, so the review
    // page crashed on `edit.venue.name`.)
    const res = await adminFetch<{
      readonly data: {
        readonly edit: VenueEdit;
        readonly currentVenue: Pick<Venue, 'id' | 'slug' | 'name'> | null;
        readonly submitter?: VenueEditSubmitter;
      };
    }>(`/admin/venues/edits/${id}`);
    const { edit, currentVenue, submitter } = res.data;
    return {
      ...edit,
      venue: currentVenue ?? { id: edit.venueId, slug: '', name: '(venue unavailable)' },
      fieldCount: Object.keys(edit.fieldChanges ?? {}).length,
      ...(submitter ? { submitter } : {}),
    };
  },

  applyEdit: (id: string): Promise<unknown> =>
    adminFetch(`/admin/venues/edits/${id}/apply`, { method: 'POST' }),

  rejectEdit: (id: string, reason: string): Promise<unknown> =>
    adminFetch(`/admin/venues/edits/${id}/reject`, {
      method: 'POST',
      body: { reason } satisfies RejectVenueEditInput,
    }),
};
